"use client";

import { PenTool, MapPin, Users, Gift } from "lucide-react";

const steps = [
  {
    number: "01",
    icon: PenTool,
    title: "Design Your Hunt",
    description: "Use our intuitive builder to create locations, clues, and challenges. Add photos, videos, and custom branding to make it uniquely yours.",
    features: ["Drag & drop builder", "Custom branding", "Rich media support"]
  },
  {
    number: "02",
    icon: MapPin,
    title: "Set Locations",
    description: "Pin locations on the map using GPS coordinates or QR codes. Create geofenced areas and set proximity requirements for each checkpoint.",
    features: ["GPS integration", "QR code support", "Geofencing"]
  },
  {
    number: "03",
    icon: Users,
    title: "Launch & Share",
    description: "Publish your hunt and share it with participants. Monitor progress in real-time with our comprehensive dashboard and analytics.",
    features: ["Real-time tracking", "Analytics dashboard", "Easy sharing"]
  },
  {
    number: "04",
    icon: Gift,
    title: "Reward Participants",
    description: "Automatically mint and distribute unique digital assets as rewards. Participants can collect, trade, and showcase their achievements.",
    features: ["Blockchain rewards", "Auto-distribution", "Achievement system"]
  }
];

export function HowItWorks() {
  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Creating engaging scavenger hunts has never been easier. 
            Follow these simple steps to launch your first adventure.
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-24">
          {steps.map((step, index) => (
            <div key={index} className={`flex flex-col lg:flex-row items-center gap-12 ${
              index % 2 === 1 ? 'lg:flex-row-reverse' : ''
            }`}>
              {/* Content */}
              <div className="flex-1 text-center lg:text-left">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-2xl font-bold text-xl mb-6">
                  {step.number}
                </div>
                
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {step.title}
                </h3>
                
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  {step.description}
                </p>

                <div className="space-y-2">
                  {step.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center justify-center lg:justify-start">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Visual */}
              <div className="flex-1">
                <div className="relative">
                  <div className="w-full max-w-md mx-auto bg-gradient-to-br from-blue-50 to-indigo-100 rounded-3xl p-8 shadow-xl">
                    <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                      <step.icon className="w-10 h-10 text-blue-600" />
                    </div>
                    
                    <div className="space-y-4">
                      <div className="h-4 bg-white rounded-full"></div>
                      <div className="h-4 bg-white rounded-full w-3/4"></div>
                      <div className="h-4 bg-white rounded-full w-1/2"></div>
                    </div>
                  </div>

                  {/* Floating elements */}
                  <div className={`absolute -top-4 ${index % 2 === 0 ? '-right-4' : '-left-4'} w-8 h-8 bg-yellow-400 rounded-lg rotate-12 hidden lg:block`}></div>
                  <div className={`absolute -bottom-4 ${index % 2 === 0 ? '-left-4' : '-right-4'} w-6 h-6 bg-green-400 rounded-full hidden lg:block`}></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-20">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Join thousands of creators who are already building amazing scavenger hunt experiences.
            </p>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Create Your First Hunt
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
